import { useState, useEffect, useRef } from 'react';
import { TYPEWRITER_SPEED_MS } from '@/lib/ai-chat/constants';

interface UseTypewriterOptions {
  speed?: number;
  enabled?: boolean;
}

interface UseTypewriterResult {
  displayedText: string;
  isComplete: boolean;
}

export const useTypewriter = (
  text: string,
  options: UseTypewriterOptions = {}
): UseTypewriterResult => {
  const { speed = TYPEWRITER_SPEED_MS, enabled = true } = options;

  // Check for reduced motion preference once
  const prefersReducedMotion = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  // Determine if we should animate on mount
  const shouldAnimateOnMount = enabled && !prefersReducedMotion;

  // Track if animation has ever started - once false, always false
  const hasStartedRef = useRef(false);
  const [displayedText, setDisplayedText] = useState(() => {
    // If not animating, show full text immediately
    if (!shouldAnimateOnMount) return text;
    hasStartedRef.current = true;
    return '';
  });
  const [isComplete, setIsComplete] = useState(!shouldAnimateOnMount);

  useEffect(() => {
    // Only run animation if we started it on mount
    if (!hasStartedRef.current) {
      // Ensure we show the full text
      setDisplayedText(text);
      setIsComplete(true);
      return;
    }

    // Animation already started on mount, run it
    let index = 0;
    const timer = setInterval(() => {
      if (index >= text.length) {
        clearInterval(timer);
        setDisplayedText(text);
        setIsComplete(true);
        return;
      }

      index++;
      setDisplayedText(text.slice(0, index));
    }, speed);

    return () => clearInterval(timer);
    // Only depend on text and speed - ignore enabled changes after mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, speed]);

  return { displayedText, isComplete };
};

export default useTypewriter;
